/**
 * ProjectDialog — shared create/edit dialog for projects.
 *
 * Used by:
 *   - Projects.tsx (list page create/edit actions)
 *   - AppShell.tsx (sidebar "+" quick-create)
 *
 * Props
 * -----
 * open          whether the modal is visible
 * mode          "create" | "edit"
 * initial       pre-filled values for edit mode (name + description)
 * workspaceId   required for create; ignored for edit (update uses projectId)
 * projectId     required for edit mode
 * onClose       called when the dialog should close (cancel or Esc)
 * onSaved       called with the saved project id after a successful write
 */

import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Modal } from "../ui/Modal";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { t } from "../i18n";

export interface ProjectDialogProps {
  open: boolean;
  mode: "create" | "edit";
  initial?: { name: string; description: string };
  workspaceId: string | null;
  projectId?: string | null;
  onClose: () => void;
  onSaved: (id: string) => void;
}

export function ProjectDialog({
  open,
  mode,
  initial,
  workspaceId,
  projectId,
  onClose,
  onSaved,
}: ProjectDialogProps) {
  const [name, setName] = useState(initial?.name ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Reset local state whenever the dialog opens with fresh initial values.
  useEffect(() => {
    if (open) {
      setName(initial?.name ?? "");
      setDescription(initial?.description ?? "");
      setSaveError(null);
    }
  }, [open, initial?.name, initial?.description]);

  function handleClose() {
    if (saving) return;
    onClose();
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setSaveError(t("projects.nameRequired"));
      return;
    }
    if (mode === "create" && !workspaceId) return;

    setSaving(true);
    setSaveError(null);

    const payload = {
      name: trimmedName,
      description: description.trim() || null,
    };

    if (mode === "create") {
      const { data, error: e } = await supabase
        .from("projects")
        .insert({ ...payload, workspace_id: workspaceId! })
        .select("id")
        .single();
      setSaving(false);
      if (e) {
        setSaveError(e.message);
        return;
      }
      onSaved(data.id as string);
    } else {
      if (!projectId) return;
      const { error: e } = await supabase
        .from("projects")
        .update(payload)
        .eq("id", projectId);
      setSaving(false);
      if (e) {
        setSaveError(e.message);
        return;
      }
      onSaved(projectId);
    }
  }

  return (
    <Modal open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>
          {mode === "create" ? t("projects.addProject") : t("projectDialog.titleEdit")}
        </h3>

        <div className="field" style={{ marginBottom: 12 }}>
          <label
            htmlFor="proj-dialog-name"
            style={{
              display: "block",
              marginBottom: 4,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {t("common.nameLabel")}
          </label>
          <Input
            id="proj-dialog-name"
            type="text"
            dir="auto"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            placeholder={t("projectDialog.namePlaceholder")}
          />
        </div>

        <div className="field" style={{ marginBottom: 12 }}>
          <label
            htmlFor="proj-dialog-desc"
            style={{
              display: "block",
              marginBottom: 4,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {t("projects.descriptionLabel")}{" "}
            <span style={{ fontWeight: 400, color: "var(--ink-3)" }}>
              {t("projects.descriptionOptional")}
            </span>
          </label>
          <textarea
            id="proj-dialog-desc"
            dir="auto"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            placeholder={t("projectDialog.descPlaceholder")}
            className="input-kit"
            style={{ resize: "vertical" }}
          />
          <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 4 }}>
            {t("projects.contextHelper")}
          </div>
        </div>

        {saveError && (
          <p className="error" style={{ fontSize: 13, marginBlock: 8 }}>
            {saveError}
          </p>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
            disabled={saving}
          >
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? t("common.saving") : mode === "create" ? t("common.add") : t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
